import { firstValueFrom } from 'rxjs';

import type {
  ReservationValidations,
  TripLeg,
} from '@/data/models/reservation.model';
import reservationService from '@/services/reservationService';
import triplegService from '@/services/triplegService';
import tripLegUtils from '@/services/triplegUtils';

import { filterConfidenceErrorsForUnsetPayload } from '../utils/filterConfidenceErrorsForUnsetPayload';
import { filterAddressErrorsForOneSidedTripLegs } from '../utils/filterAddressErrorsForOneSidedTripLegs';
import {
  expandTripLegScoreError,
  expandTripLegScoreValidationErrors,
  isLowConfidenceError,
  isRoundTripReturnLeg,
  mergeTripLegConfidenceResponsesIntoReservationPayload,
  mergeTripLegConfidenceResponsesIntoTripLegs,
  resolveConfidenceErrorFieldPath,
  type TripLegConfidenceErrorPayload,
  type TripLegConfidenceStoredEntry,
  type TripLegScoreCacheEntry,
  type TripLegScoreErrorBody,
} from './tripLegConfidence';
import {
  enrichReservationMissingPlaceIds,
  type ResolvedFormAddress,
  type ResolvePlaceFn,
} from './resolveMissingPlaceIds';

const EMPTY_VALIDATIONS: ReservationValidations = {
  tripLegUpdates: [],
  errors: [],
  warnings: [],
  log: [],
};

export type BundledValidationInput = {
  reservation: unknown;
  addressValidationEnabled: boolean;
  /** Resolve the place id from the display text. See: F1V2-6170 */
  resolvePlace?: ResolvePlaceFn;
  /** Per-trip PlaceType (or string) currently on the pickup address field machine. */
  selectedPickups?: unknown[];
  /** Per-trip PlaceType (or string) currently on the dropoff address field machine. */
  selectedDropoffs?: unknown[];
};

export type BundledValidationOutput = {
  validations: ReservationValidations;
  confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry>;
  /**
   * /score/trip-leg confidence errors keyed by trip-leg index, each a list of
   * standalone errors (a rejected call's aggregate expanded, or a 200's
   * `validationErrors`).
   */
  errorsByTripIndex: Record<number, TripLegConfidenceErrorPayload[]>;
  /**
   * Saved passenger address selections that were missing a Google place id
   * and got resolved during this run. We need to write these back onto the form
   * so the user's saved address selections survive into submit. See: F1V2-6170
   */
  resolvedFormAddresses?: ResolvedFormAddress[];
};

/**
 * Address-keyed cache of in-flight / settled /score/trip-leg calls. Keyed by the
 * confidence payload (addresses only — the score is a pure function of them), so
 * concurrent or repeated runs for the same address share one network call. It's
 * module-scoped on purpose: a bundled run that gets cancelled mid-flight (e.g.
 * the estimated-pickup auto-fill firing a second FIELD_EDITED right after an
 * address change) would otherwise discard its result and the next run would
 * re-score. Cleared on form reset via `clearTripLegScoreCache`.
 */
const tripLegScoreCache = new Map<string, Promise<TripLegScoreCacheEntry>>();

export function clearTripLegScoreCache(): void {
  tripLegScoreCache.clear();
}

function scoreTripLeg(
  payload: ReturnType<typeof tripLegUtils.getTripLegConfidencePayload>,
  hasPickup: boolean,
  hasDropoff: boolean,
  // Part of the cache key: the response differs by it.
  throwConfidenceError: boolean,
): Promise<TripLegScoreCacheEntry> {
  const key = `${JSON.stringify(payload)}|throwConfidenceError=${throwConfidenceError}`;
  const cached = tripLegScoreCache.get(key);
  if (cached) {
    return cached;
  }
  const promise = firstValueFrom(
    triplegService.validateTripLegAddress(payload, { throwConfidenceError }),
  )
    .then(
      (response): TripLegScoreCacheEntry => ({
        entry: { response, hasPickup, hasDropoff },
      }),
    )
    .catch((err: unknown): TripLegScoreCacheEntry => {
      const error: TripLegScoreErrorBody =
        err && typeof err === 'object'
          ? (err as TripLegScoreErrorBody)
          : { message: String(err) };
      // A rejected call has no confidence data, so it blocks and skips
      // /validate. Cache deterministic backend errors (they carry an
      // errorCode); evict transient failures so the next run retries.
      if (!error.errorCode) {
        tripLegScoreCache.delete(key);
      }
      return { error };
    });
  tripLegScoreCache.set(key, promise);
  return promise;
}

/**
 * Whether a leg side carries a usable street line. Used by the **save-time**
 * scorer, which is best-effort: a leg it skips is simply sent unscored, so the
 * stricter rule costs nothing there.
 */
function hasAddressContent(address: unknown): boolean {
  const line1 = (address as { addressLine1?: unknown } | null | undefined)
    ?.addressLine1;
  return typeof line1 === 'string' && line1.trim().length > 0;
}

/**
 * Whether the user has *selected* an address for this side of the leg — the
 * live-validation gate for `/score/trip-leg`.
 *
 * Deliberately weaker than `hasAddressContent`: it asks "is there a selection"
 * rather than "is the selection good". The payload builder
 * (`reservationUtils.getTripLegFromData` + `removeEmptyAttributes`) drops the
 * address key entirely for an untouched side, so an empty/absent object still
 * means "not entered yet" and the leg is still skipped while half-filled.
 *
 * Why not the street-line rule here: a Google place with no `street_number` /
 * `route` (e.g. a `colloquial_area` like "Fairfax Park") converts to a blank
 * `addressLine1` but is otherwise fully populated. Under the stricter rule the
 * leg was never scored, so the only thing that could flag the address was
 * `/validate` — which rejects such a payload at the request-schema layer (HTTP
 * 400), a failure this function's caller swallows. The result was no blocking
 * error anywhere and an enabled Next button over a visibly invalid address.
 * Letting the leg reach `/score/trip-leg` puts it on the same path as every
 * other bad address: the score error blocks Next and renders on the field.
 *
 * Matches `filterAddressErrorsForOneSidedTripLegs`'s notion of "has address",
 * so the scoring gate and the error filter agree on what a one-sided leg is.
 */
function hasAddressSelection(address: unknown): boolean {
  return (
    typeof address === 'object' &&
    address !== null &&
    Object.keys(address as object).length > 0
  );
}

/**
 * Scores a bare tripLegs array (the POST /updateTripLegs shape) via
 * /score/trip-leg and merges the resulting `confidence` / address
 * `confidenceScore` blocks into each leg — the update-path counterpart of the
 * merge the create-reservation submit performs on its payload.
 *
 * Best-effort by design: a leg whose score call fails — or that is missing
 * either address and therefore isn't scored at all — is sent unchanged
 * (`confidence` / `confidenceScore` are optional on the backend contract), so
 * the update itself still runs and any real address problem surfaces through
 * /updateTripLegs' own validation. Shares the module-level score cache, so a
 * save right after a bundled validation of the same addresses makes no extra
 * network calls.
 */
export async function scoreAndMergeConfidenceIntoTripLegs(
  tripLegs: TripLeg[],
  throwConfidenceError: boolean,
): Promise<TripLeg[]> {
  try {
    const confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry> =
      {};
    await Promise.all(
      tripLegs.map(async (leg, index) => {
        const hasPickup = hasAddressContent(leg?.pickUpAddress);
        const hasDropoff = hasAddressContent(leg?.dropOffAddress);
        // /score/trip-leg rejects requests missing either address — only score
        // legs with BOTH sides present.
        if (!hasPickup || !hasDropoff) return;

        const result = await scoreTripLeg(
          tripLegUtils.getTripLegConfidencePayload(leg),
          hasPickup,
          hasDropoff,
          throwConfidenceError,
        );
        if (result.entry) {
          confidenceByTripIndex[index] = result.entry;
        }
      }),
    );
    return mergeTripLegConfidenceResponsesIntoTripLegs(
      tripLegs,
      confidenceByTripIndex,
    ) as TripLeg[];
  } catch {
    // Never block the update on a scoring problem — send the legs unscored.
    return tripLegs;
  }
}

/**
 * Single, sequential validation pass shared by every field edit:
 *  0. if a selected pickup/dropoff has no Google place id, resolve it from
 *     the display text (saved passenger picks) and stamp the structured
 *     address onto the payload;
 *  1. score every trip leg that has BOTH addresses via /score/trip-leg first
 *     (deduped/cached per address; the endpoint rejects one-sided requests);
 *     a 200's `validationErrors` are collected like any other score error;
 *  2. if any score call was rejected, skip /validate — without that leg's
 *     confidence data it only produces spurious address errors;
 *  3. otherwise merge the confidence data into the payload and call /validate.
 *
 * Both calls resolve before this returns, so the caller applies a single
 * aggregated result in one transition (no flickering between intermediate states).
 */
export async function runBundledValidation(
  input: BundledValidationInput,
  signal?: AbortSignal,
): Promise<BundledValidationOutput> {
  const { addressValidationEnabled } = input;
  if (input.reservation == null) {
    throw new Error('runBundledValidation: missing reservation payload');
  }

  // Ingested passenger addresses won't have a Google place_id (AddressInput writes
  // an empty *AddressStruct for them, causing issues. See: F1V2-6170). Resolve them
  // from the selected display text before scoring /validate so the payload has line/city/zip.
  let reservation: unknown = input.reservation;
  let resolvedFormAddresses: ResolvedFormAddress[] = [];
  if (input.resolvePlace) {
    const enriched = await enrichReservationMissingPlaceIds({
      reservation,
      selectedPickups: input.selectedPickups ?? [],
      selectedDropoffs: input.selectedDropoffs ?? [],
      resolvePlace: input.resolvePlace,
    });
    reservation = enriched.reservation;
    resolvedFormAddresses = enriched.resolvedFormAddresses;
  }

  // Address validation disabled → legacy /validate-only behavior.
  if (!addressValidationEnabled) {
    const validations = await firstValueFrom(
      reservationService.validateReservation(reservation, signal),
    );
    return {
      validations,
      confidenceByTripIndex: {},
      errorsByTripIndex: {},
      resolvedFormAddresses,
    };
  }

  const tripLegs = ((reservation as { tripLegs?: unknown[] }).tripLegs ??
    []) as Array<Record<string, unknown>>;
  const confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry> =
    {};
  const errorsByTripIndex: Record<number, TripLegConfidenceErrorPayload[]> = {};
  // Round-trip return-leg score errors are buffered separately and only
  // surfaced when no other leg errored — see the merge step after scoring.
  const returnLegErrorsByTripIndex: Record<
    number,
    TripLegConfidenceErrorPayload[]
  > = {};
  // A rejected score call leaves its leg without confidence data (step 2).
  let scoreCallFailed = false;

  // 1. Score every leg first (shared cache dedupes identical addresses).
  await Promise.all(
    tripLegs.map(async (leg, index) => {
      const hasPickup = hasAddressSelection(leg?.pickUpAddress);
      const hasDropoff = hasAddressSelection(leg?.dropOffAddress);
      // /score/trip-leg rejects requests missing either address — only score
      // legs with BOTH sides selected. Without this gate every partially
      // entered leg would produce a blocking score error and skip /validate.
      // One-sided legs go to /validate unscored; the resulting noise is
      // dropped by filterConfidenceErrorsForUnsetPayload and
      // filterAddressErrorsForOneSidedTripLegs.
      //
      // "Selected", not "has a street line": a selection whose addressLine1 is
      // blank is still scored, so a bad address is reported by the endpoint
      // that can report it. See `hasAddressSelection`.
      if (!hasPickup || !hasDropoff) return;

      const payload = tripLegUtils.getTripLegConfidencePayload(leg);
      const result = await scoreTripLeg(payload, hasPickup, hasDropoff, false);
      if (result.entry) {
        confidenceByTripIndex[index] = result.entry;
      }
      if (result.error) {
        scoreCallFailed = true;
      }
      // A rejected call's aggregate and a 200's `validationErrors` land in
      // the same channel.
      const errors = result.error
        ? expandTripLegScoreError(result.error)
        : expandTripLegScoreValidationErrors(
            result.entry?.response.validationErrors,
          );
      if (errors.length > 0) {
        // Resolve each error's form field once, here — the only place with
        // the leg and its index. Round-trip return-leg errors are buffered:
        // whether they surface depends on the outbound leg's result.
        const target = isRoundTripReturnLeg(leg)
          ? returnLegErrorsByTripIndex
          : errorsByTripIndex;
        target[index] = errors.map((err) => ({
          ...err,
          formFieldPath: resolveConfidenceErrorFieldPath(index, leg, err),
        }));
      }
    }),
  );

  // A round trip's return leg mirrors the outbound addresses onto the same
  // form row, so behind a hard outbound error its errors would only paint the
  // same bad pair on the row's other field — suppress them. A low-confidence
  // outbound entry may be a non-blocking warning and says nothing about the
  // return call, so return-leg errors (including a rejected call) surface.
  const outboundHasHardError = Object.values(errorsByTripIndex)
    .flat()
    .some((err) => !isLowConfidenceError(err));
  if (!outboundHasHardError) {
    Object.assign(errorsByTripIndex, returnLegErrorsByTripIndex);
  }

  // 2. A rejected score call → skip /validate (no confidence data to merge).
  // A 200 with `validationErrors` has the data and proceeds; Next/Done gating
  // on those errors is `hasBlockingTripLegConfidenceError`.
  if (scoreCallFailed) {
    return {
      validations: EMPTY_VALIDATIONS,
      confidenceByTripIndex,
      errorsByTripIndex,
      resolvedFormAddresses,
    };
  }

  // 3. Merge confidence and run /validate.
  const merged = mergeTripLegConfidenceResponsesIntoReservationPayload(
    reservation,
    confidenceByTripIndex,
  );
  let validations: ReservationValidations = EMPTY_VALIDATIONS;
  try {
    const result = await firstValueFrom(
      reservationService.validateReservation(merged, signal),
    );
    validations = {
      ...result,
      errors: filterAddressErrorsForOneSidedTripLegs(
        filterConfidenceErrorsForUnsetPayload(result.errors, merged),
        merged,
      ),
    };
  } catch {
    // /validate failed (e.g. backend address-lookup error). Don't lose the score
    // results — return them with no validate errors so the trip-leg warnings/
    // errors still render and Next gating stays consistent. (If the parent
    // cancelled this run, XState ignores this resolution anyway.)
  }

  return {
    validations,
    confidenceByTripIndex,
    errorsByTripIndex,
    resolvedFormAddresses,
  };
}
